import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Briefcase, CheckCircle2, Lock, MessageSquare, RefreshCw } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { PageHeader } from '@/app/DashboardLayout';
import { formatDateTime, currentMonth, type Period } from '@/lib/dates';
import {
  Badge, Button, Card, EmptyState, ErrorState, Money, PeriodPicker, SkeletonTable,
  TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';

interface PracticeClient {
  orgId: string;
  name: string;
  role: string;
  gstin: string | null;
  gstRegime: 'none' | 'gst';
  stateCode: string | null;
  currency: string;
  periodStatus: { locked: boolean; reason: string | null };
  receivables: string;
  overdue: string;
  invoiceCount: number;
  openQueries: number;
  lastActivityAt: string | null;
  findings?: { high: number; medium: number; low: number; total: number };
  findingsError?: string;
}

/**
 * Every client, on one screen.
 *
 * ── WHY THIS IS THE FIRST PAGE A CA SEES ──
 * A practising accountant works across thirty to a hundred businesses. Before
 * this page the only way to answer "which of my clients needs attention this
 * month" was to switch into each one and look. Forty switches is not a
 * workflow, it is a reason to use something else.
 *
 * ── THE FINDINGS COLUMN IS OPT-IN, AND SAYS SO ──
 * Running fourteen checks across forty clients is forty period loads. The list
 * opens instantly without them and fills the column on request. What it does
 * NOT do is show a cheaper approximation - the badge and the review page it
 * links to are the same calculation, so a number here is a number you can act
 * on.
 */
export function PracticePage() {
  const switchTo = useSwitchOrg();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const [period, setPeriod] = useState<Period>(() => currentMonth());
  const [withFindings, setWithFindings] = useState(false);

  const query = useQuery({
    queryKey: ['practice', period.from, period.to, withFindings],
    queryFn: () =>
      api.get<{ items: PracticeClient[]; period: { from: string; to: string } }>(
        `/practice/clients${qs({ from: period.from, to: period.to, withFindings: withFindings || undefined })}`,
      ),
  });

  const clients = query.data?.items ?? [];
  const totalHigh = clients.reduce((sum, c) => sum + (c.findings?.high ?? 0), 0);

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Every business you work on, and what needs looking at before filing."
        actions={
          <Button
            size="sm"
            variant={withFindings ? 'secondary' : 'outline'}
            onClick={() => setWithFindings((on) => !on)}
            disabled={query.isFetching}
          >
            <RefreshCw className={query.isFetching ? 'size-4 animate-spin' : 'size-4'} aria-hidden />
            {withFindings ? 'Findings on' : 'Run review on all'}
          </Button>
        }
      />

      <div className="space-y-4 px-5 pb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PeriodPicker value={period} onChange={setPeriod} />
          {withFindings && !query.isFetching ? (
            <p className="text-sm text-muted-foreground">
              {totalHigh > 0 ? (
                <span className="font-medium text-destructive">
                  {totalHigh} thing{totalHigh === 1 ? '' : 's'} to fix before filing
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 font-medium text-success">
                  <CheckCircle2 className="size-4" aria-hidden />
                  Nothing serious across any client
                </span>
              )}
            </p>
          ) : null}
        </div>

        <Card>
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : query.isLoading ? (
            <SkeletonTable rows={6} cols={6} />
          ) : clients.length ? (
            <Table>
              <THead>
                <TR>
                  <TH>Client</TH>
                  <TH>GST</TH>
                  <TH>Period</TH>
                  {withFindings ? <TH>To fix</TH> : null}
                  <TH numeric>Receivables</TH>
                  <TH numeric>Overdue</TH>
                  <TH>Last activity</TH>
                  <TH>{''}</TH>
                </TR>
              </THead>
              <TBody>
                {clients.map((client) => (
                  <TR key={client.orgId}>
                    <TD>
                      <div className="font-medium">{client.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {client.role === 'admin' ? 'Administrator' : 'Member'}
                        {client.openQueries > 0 ? (
                          <span className="ml-2 inline-flex items-center gap-1 text-warning">
                            <MessageSquare className="size-3" aria-hidden />
                            {client.openQueries} open quer{client.openQueries === 1 ? 'y' : 'ies'}
                          </span>
                        ) : null}
                      </div>
                    </TD>
                    <TD className="text-muted-foreground">
                      {client.gstRegime === 'gst' ? (
                        <span className="font-mono text-xs">{client.gstin ?? `${client.stateCode ?? '—'} · no GSTIN`}</span>
                      ) : (
                        <span className="text-xs">Not registered</span>
                      )}
                    </TD>
                    <TD>
                      {client.periodStatus.locked ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                          title={client.periodStatus.reason ?? undefined}
                        >
                          <Lock className="size-3" aria-hidden />
                          Filed
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {client.invoiceCount} invoice{client.invoiceCount === 1 ? '' : 's'}
                        </span>
                      )}
                    </TD>
                    {withFindings ? (
                      <TD>
                        <FindingsCell client={client} />
                      </TD>
                    ) : null}
                    <TD numeric>
                      <Money value={client.receivables} currency={client.currency} />
                    </TD>
                    <TD numeric>
                      {Number(client.overdue) > 0 ? (
                        <Money
                          value={client.overdue}
                          currency={client.currency}
                          className="font-medium text-destructive"
                        />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TD>
                    <TD className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDateTime(client.lastActivityAt)}
                    </TD>
                    <TD>
                      {/* Through the hook, not a Link that sets the org on
                          the way: that left every cached query keyed to the
                          previous client, and the review page opened with the
                          right name over the wrong findings until each key
                          happened to refetch. */}
                      <button
                        type="button"
                        onClick={() => switchTo(client.orgId, '/review')}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {client.orgId === activeOrgId ? 'Review' : 'Open'}
                      </button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState
              icon={Briefcase}
              title="No clients yet"
              description="Every business you are invited to appears here, with its receivables and anything that needs fixing before filing."
            />
          )}
        </Card>

        <p className="text-xs text-muted-foreground">
          This list is built from the businesses you are a member of. Ask a client to invite you and
          they appear here.
        </p>
      </div>
    </>
  );
}

function FindingsCell({ client }: { client: PracticeClient }) {
  if (client.findingsError) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={client.findingsError}>
        <AlertTriangle className="size-3" aria-hidden />
        Could not run
      </span>
    );
  }
  const findings = client.findings;
  if (!findings) return <span className="text-xs text-muted-foreground">—</span>;
  if (findings.total === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-success">
        <CheckCircle2 className="size-3" aria-hidden />
        Clean
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {findings.high > 0 ? <Badge variant="danger">{findings.high} high</Badge> : null}
      {findings.medium > 0 ? <Badge variant="warning">{findings.medium} medium</Badge> : null}
      {findings.low > 0 ? <Badge variant="neutral">{findings.low} low</Badge> : null}
    </div>
  );
}
